/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/KubernetesEndpoint/Components/KubernetesEndpointEditorComponent" />
import * as React from "react";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssPageService } from "VSS/Platform/Page";
import { ServiceEndpoint, ServiceEndpointRequest, ServiceEndpointRequestResult } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { AzureSubscriptionAuthComponent } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/KubernetesEndpoint/Components/AzureSubscriptionAuthComponent";
import { AzureSubscriptionManualAuthComponent } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/KubernetesEndpoint/Components/AzureSubscriptionManualAuthComponent";
import { EndpointDetailsComponent } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/KubernetesEndpoint/Components/EndpointDetailsComponent";
import { KubeConfigAuthComponent } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/KubernetesEndpoint/Components/KubeConfigAuthComponent";
import { ServiceAccountAuthComponent } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/KubernetesEndpoint/Components/ServiceAccountAuthComponent";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Editor/Resources";
import { AzureSubscriptionAuthActionCreator } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/KubernetesEndpoint/Actions/AzureSubscriptionAuthActionCreator";
import * as KubernetesEndpointConstants from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/KubernetesEndpoint/KubernetesEndpointConstants";
import { IEndpointEditorComponentProps } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointsEditorPanelInterfaces";
import { cloneEndpoint, EndpointsEditorPanelUtils } from "DistributedTaskUI/ServiceEndpoints/Editor/Utils/EndpointsEditorPanelUtils";
import { GenericFooterComponent } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/GenericEndpointComponents/GenericFooterComponent";
import { Observer } from "VSSUI/Observer";
import { ObservableValue } from "VSS/Core/Observable";
import { EndpointsEditorActionsCreator } from "DistributedTaskUI/ServiceEndpoints/Editor/Actions/EndpointsEditorActionsCreator";
import { EndpointsEditorStore } from "DistributedTaskUI/ServiceEndpoints/Editor/Stores/EndpointsEditorStore";
import { ActionsCreatorInstanceIds } from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import * as Constants from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import * as Utils_String from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import * as Utils_Accessibility from "VSS/Core/Util/Accessibility";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";

export interface IKubernetesEndpointEditorComponentProps extends IEndpointEditorComponentProps {
    hidePanelFooter: (hideFooter: boolean) => void;
    closePanel: (serviceEndpoint: ServiceEndpoint) => void;
    onBackButtonClick: () => void;
    hideBackButton: boolean;
    isVerifiableConnection: boolean;
    updateError: (isError: boolean, errorMessage: string, errorStatus?: string) => void;
    errorMessage: string;
}

export interface IKubernetesEndpointEditorComponentState {
    verifyInProgress: boolean;
    saveInProgress: boolean;
    setupInProgress: boolean;
    isError: boolean;
    errorMessage?: string;
    isVerifyInProgress?: boolean;
    isVerificationSuccess?: boolean;
}

export class KubernetesEndpointEditorComponent extends VssComponent<IKubernetesEndpointEditorComponentProps, IKubernetesEndpointEditorComponentState>{
    constructor(props: IKubernetesEndpointEditorComponentProps, context: IVssComponentContext) {
        super(props, context);
        this.state = { isError: false, verifyInProgress: false, saveInProgress: false, setupInProgress: false };
        this._draftServiceEndpoint = cloneEndpoint(this.props.draftEndpoint);
        this._initialEndpointState = cloneEndpoint(this.props.draftEndpoint);
        this._endpointEditorActionCreator = ActionCreatorManager.GetActionCreator<EndpointsEditorActionsCreator>(EndpointsEditorActionsCreator, ActionsCreatorInstanceIds.EndpointsEditorActionsCreatorInstanceId);
        this._endpointEditorStore = StoreManager.GetStore<EndpointsEditorStore>(EndpointsEditorStore, ActionsCreatorInstanceIds.EndpointsEditorStoreInstanceId);
        this._endpointEditorStore.addChangedListener(this._onEndpointEditorStoreChange);
        this._azureSubscriptionAuthActionCreator = ActionCreatorManager.CreateActionCreator<AzureSubscriptionAuthActionCreator, IVssPageContext>
            (AzureSubscriptionAuthActionCreator, KubernetesEndpointConstants.KubernetesEndpointEditorInstanceId, this.context.pageContext);
        this._showAzureSubscriptionAuth = this._showAzureSubscriptionAuthOption();

        this._isSovereignFeatureEnabled = isFeatureFlagEnabled(context.pageContext,
            Constants.FeatureAvailabilityFlags.EnableSovereignSupportInKubernetesEndpoint, false);

        if (!this.props.isUpdateForm) {
            this._resetEndpointState();
        }

        this._reloadFooter = new ObservableValue(false);
        this._isSaveEnabled = this._shouldShowUpdateForm() ? false : true;
        this._invalidComponentCount = 0;
        this._dirtyComponentCount = 0;
        this.props.hidePanelFooter(true);
    }

    public componentWillReceiveProps(props: IKubernetesEndpointEditorComponentProps) {
        this._draftServiceEndpoint = cloneEndpoint(props.draftEndpoint);
    }

    public render(): React.ReactNode {
        return (
            <React.Fragment>
                <div>
                    {this._getAuthPicker()}
                    {this._getInputsForm()}
                    {/* This verificationcomponent is being rendered in other endpoint components as well. Ensure to make similar changes at other places if this is updated */}
                    {this._getVerificationComponent()}
                    <EndpointDetailsComponent
                        {...this.props}
                        draftEndpoint={this._draftServiceEndpoint}
                        isUpdateForm={this._shouldShowUpdateForm()}
                        updateValidAndDirtyStateChange={this._handleValidAndDirtyStateChange}
                        instanceId={"EndpointDetailsComponent"}
                        updateAllowAllPipelinesFlag={this.props.updateAllowAllPipelinesFlag}
                        allowAllPipelinesFlagState={this.props.allowAllPipelinesFlagState}
                    />
                </div>
                { this.state.isError && <MessageCard
                    className="flex-self-stretch margin-top-16 margin-bottom-16"
                    severity={MessageCardSeverity.Error}
                   >
                   {this.state.errorMessage}
                </MessageCard>}
                {this._getFooterComponent()}
            </React.Fragment>
        );
    }

    private _getAuthPicker(): React.ReactNode {
        let _selectButtonId = this._getAuthorizationKey();
        return (
            <RadioButtonGroup
                onSelect={this._onAuthSelectionChange}
                selectedButtonId={_selectButtonId}
                text={Resources.AuthenticationPickerHeader}>
                <RadioButton
                    id={KubernetesEndpointConstants.KubernetesEndpointAuthMethods.Kubeconfig}
                    text={Resources.KubeConfig}
                    key={KubernetesEndpointConstants.KubernetesEndpointAuthMethods.Kubeconfig} />
                <RadioButton
                    id={KubernetesEndpointConstants.KubernetesEndpointAuthMethods.ServiceAccount}
                    text={Resources.ServiceAccount}
                    key={KubernetesEndpointConstants.KubernetesEndpointAuthMethods.ServiceAccount} />
                {this._showAzureSubscriptionAuth && <RadioButton
                    id={KubernetesEndpointConstants.KubernetesEndpointAuthMethods.AzureSubscription}
                    text={Resources.AzureSubscription}
                    key={KubernetesEndpointConstants.KubernetesEndpointAuthMethods.AzureSubscription} />
                }
                {this._showAzureSubscriptionAuth && this._isSovereignFeatureEnabled && <RadioButton
                    id={KubernetesEndpointConstants.KubernetesEndpointAuthMethods.AzureSubscriptionManual}
                    text={Resources.AzureSubscriptionManual}
                    key={KubernetesEndpointConstants.KubernetesEndpointAuthMethods.AzureSubscriptionManual} />
                }
            </RadioButtonGroup>
        );
    }

    private _getInputsForm(): React.ReactNode {
        let authorizationType = this._getAuthorizationKey();
        switch (authorizationType) {
            case KubernetesEndpointConstants.KubernetesEndpointAuthMethods.Kubeconfig:
                return (
                    <KubeConfigAuthComponent
                        {...this.props}
                        draftEndpoint={this._draftServiceEndpoint}
                        isUpdateForm={this._shouldShowUpdateForm()}
                        updateValidAndDirtyStateChange={this._handleValidAndDirtyStateChange}
                        instanceId={"KubeConfigAuthComponent"} />
                );

            case KubernetesEndpointConstants.KubernetesEndpointAuthMethods.ServiceAccount:
                return (
                    <ServiceAccountAuthComponent
                        {...this.props}
                        draftEndpoint={this._draftServiceEndpoint}
                        isUpdateForm={this._shouldShowUpdateForm()}
                        updateValidAndDirtyStateChange={this._handleValidAndDirtyStateChange}
                        instanceId={"ServiceAccountAuthComponent"} />
                );

            case KubernetesEndpointConstants.KubernetesEndpointAuthMethods.AzureSubscription:
                return (
                    <AzureSubscriptionAuthComponent
                        {...this.props}
                        draftEndpoint={this._draftServiceEndpoint}
                        isUpdateForm={this._shouldShowUpdateForm()}
                        updateValidAndDirtyStateChange={this._handleValidAndDirtyStateChange} />
                );

            case KubernetesEndpointConstants.KubernetesEndpointAuthMethods.AzureSubscriptionManual:
                this._draftServiceEndpoint.data.authorizationType = KubernetesEndpointConstants.KubernetesEndpointAuthMethods.AzureSubscription;
                return (
                    <AzureSubscriptionManualAuthComponent
                        {...this.props}
                        draftEndpoint={this._draftServiceEndpoint}
                        isUpdateForm={this._shouldShowUpdateForm()}
                        updateValidAndDirtyStateChange={this._handleValidAndDirtyStateChange}
                        instanceId={"AzureSubscriptionManualAuthComponent"} />
                );
        }
    }

    private _getVerificationComponent(): React.ReactNode {
        if (this.props.isVerifiableConnection && Utils_String.equals(this._draftServiceEndpoint.data.authorizationType, KubernetesEndpointConstants.KubernetesEndpointAuthMethods.Kubeconfig, true)) {
            const isVerificationDisabled: boolean = (this.state && this.state.isVerifyInProgress) || !this._draftServiceEndpoint.authorization.parameters["kubeConfig"] || this._invalidComponentCount > 1;
            return (
                <div className={"flex-row default-input-margin-top"}>
                    { EndpointsEditorPanelUtils.getVerifyButton(isVerificationDisabled, this._verifyEndpoint) }
                    { this.state && EndpointsEditorPanelUtils.getVerificationDetails(this.state.isVerifyInProgress, this.state.isVerificationSuccess, this.props.errorMessage) }
                </div>
            );
        } else {
            return null;
        }
    }

    private _verifyEndpoint = (): void => {
        this._updateVerificationStatus(true, undefined);
        this._verifyConnection().then((serviceEndpointRequestResult: ServiceEndpointRequestResult) => {
            if (serviceEndpointRequestResult.errorMessage) {
                this._updateVerificationStatus(false, false);
                this.props.updateError(true, serviceEndpointRequestResult.errorMessage, Resources.VerificationFailed);
            } else {
                this._updateVerificationStatus(false, true);
                this.props.updateError(false, "");
            }
        }).catch((error) => {
            this._updateVerificationStatus(false, false);
            const errorMessage: string = error.message ? error.message : Resources.ValidateEndpointErrorMessage;
            this.props.updateError(true, errorMessage, Resources.VerificationFailed);
        });
    }

    private _updateVerificationStatus = (isVerifyInProgress: boolean, isVerificationSuccess: boolean): void => {
        this.setState({
            isVerifyInProgress: isVerifyInProgress,
            isVerificationSuccess: isVerificationSuccess
        });
    }

    private _verifyConnection(): Promise<ServiceEndpointRequestResult> {
        const serviceEndpointRequest: ServiceEndpointRequest = EndpointsEditorPanelUtils.getServiceEndpointRequestForTest(this._draftServiceEndpoint);
        return this._endpointEditorActionCreator.verifyServiceEndpoint(serviceEndpointRequest, this._draftServiceEndpoint.id);
    }

    private _getFooterComponent(): React.ReactNode {
        return (
            <Observer reloadFooter={this._reloadFooter}>
                {(props: { reloadFooter: boolean }) => {
                    return (
                        <GenericFooterComponent
                            isUpdateForm={this.props.isUpdateForm}
                            enableSave={this._isSaveEnabled}
                            isVerifiable={this._isVerifiableAuthType()}
                            showSaveButton={true}
                            showBackButton={!this.props.hideBackButton}
                            verifyInProgress={this.state.verifyInProgress}
                            saveInProgress={this.state.saveInProgress}
                            setupInProgress={this.state.setupInProgress}
                            onVerifyAndSaveButtonClick={this._verifyAndSaveServiceEndpoint}
                            onSaveButtonClick={this._saveServiceEndpoint}
                            onBackButtonClick={this.props.onBackButtonClick} />
                    );
                }}
            </Observer>
        );
    }

    private _onAuthSelectionChange = (authorizationKey: string) => {
        this._resetEndpointState(authorizationKey);
        if (authorizationKey === KubernetesEndpointConstants.KubernetesEndpointAuthMethods.AzureSubscriptionManual) {
            this._draftServiceEndpoint.authorization.parameters.spnCreationMethod = KubernetesEndpointConstants.SpnCreationMethod.Manual;
        }
        this.props.updateDraftEndpointState(this._draftServiceEndpoint);
    }

    private _getAuthorizationKey() {
        let authorizationKey = this._draftServiceEndpoint.data.authorizationType;
        let spnCreationMethod = this._draftServiceEndpoint.authorization.parameters.spnCreationMethod;
        if (authorizationKey === KubernetesEndpointConstants.KubernetesEndpointAuthMethods.AzureSubscription && spnCreationMethod === KubernetesEndpointConstants.SpnCreationMethod.Manual) {
            authorizationKey = this._draftServiceEndpoint.data.authorizationType + spnCreationMethod;
        }
        return authorizationKey;
    }

    private _shouldShowUpdateForm() {
        return this.props.isUpdateForm &&
            this._draftServiceEndpoint.data.authorizationType === this._initialEndpointState.data.authorizationType;
    }

    private _getSelectedAuthenticationScheme = (authorizationType: string): string => {
        return (authorizationType === KubernetesEndpointConstants.KubernetesEndpointAuthMethods.ServiceAccount) ? "Token" : "Kubernetes";
    }

    private _resetEndpointState = (authorizationType?: string) => {
        if (this.props.isUpdateForm && authorizationType === this._initialEndpointState.data.authorizationType) {
            this._draftServiceEndpoint.data = this._initialEndpointState.data;
            this._draftServiceEndpoint.authorization.parameters = this._initialEndpointState.authorization.parameters;
        }
        else {
            this._isSaveEnabled = true;
            this._draftServiceEndpoint.data = {};
            this._draftServiceEndpoint.authorization.parameters = {};
        }

        let authType = authorizationType;
        if (!authType) {
            authType = this._showAzureSubscriptionAuth ? KubernetesEndpointConstants.KubernetesEndpointAuthMethods.AzureSubscription : KubernetesEndpointConstants.KubernetesEndpointAuthMethods.Kubeconfig;
        }

        this._draftServiceEndpoint.data.authorizationType = authType;
        this._draftServiceEndpoint.authorization.scheme = this._getSelectedAuthenticationScheme(authType);
        this.setTimeout(() => {
            this.props.updateDraftEndpointState(this._draftServiceEndpoint);
        });
    }

    private _showAzureSubscriptionAuthOption() {
        const vssPageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();
        return vssPageData.isHosted;
    }

    private _verifyAndSaveServiceEndpoint = () => {
        this._endpointEditorActionCreator.verifyAndSaveServiceEndpoint(this._draftServiceEndpoint, "TestConnection", this.props.allowAllPipelinesFlagState, this.props.isUpdateForm);
    }

    private _saveServiceEndpoint = () => {
        if (this._draftServiceEndpoint.data.authorizationType === KubernetesEndpointConstants.KubernetesEndpointAuthMethods.AzureSubscription
            && this._draftServiceEndpoint.data["operation.type"] === "update"
        ) {
            const azureSubscriptionTenantId = this._draftServiceEndpoint.authorization.parameters.azureTenantId;
            this._azureSubscriptionAuthActionCreator.getAccessTokenAsync(azureSubscriptionTenantId).then((accessToken) => {
                this._draftServiceEndpoint.authorization.parameters.azureAccessToken = accessToken;
                this._endpointEditorActionCreator.customSaveServiceEndpoint(this._draftServiceEndpoint, this.props.allowAllPipelinesFlagState, this.props.isUpdateForm);
            }, error => {
                if (error.message) {
                    Utils_Accessibility.announce(error.message);
                }
                this.setState({ errorMessage: error.message ? error.message : JSON.stringify(error) });
            });
        }
        else {
            this._endpointEditorActionCreator.customSaveServiceEndpoint(this._draftServiceEndpoint, this.props.allowAllPipelinesFlagState, this.props.isUpdateForm);
        }
    }

    private _onEndpointEditorStoreChange = () => {
        const storeState = this._endpointEditorStore.getState();

        if (this.state.saveInProgress && !storeState.setupInProgress && !storeState.saveInProgress && !storeState.errorMessage) {
            setTimeout(() => {
                this.props.closePanel(storeState.serviceEndpoint);
            });
        }

        if (this.state.setupInProgress && !storeState.setupInProgress && !storeState.errorMessage) {
            setTimeout(() => {
                this.props.closePanel(storeState.serviceEndpoint);
            });
        }

        this.setState({
            verifyInProgress: storeState.verifyInProgress,
            saveInProgress: storeState.saveInProgress,
            setupInProgress: storeState.setupInProgress,
            isError: storeState.isError,
            errorMessage: storeState.errorMessage
        });
    }

    private _isVerifiableAuthType(): boolean {
        return this._draftServiceEndpoint.data.authorizationType === KubernetesEndpointConstants.KubernetesEndpointAuthMethods.Kubeconfig;
    }

    private _handleValidAndDirtyStateChange = (isValid?: boolean, isDirty?: boolean) => {
        if (isValid !== undefined) {
            this._invalidComponentCount += isValid ? -1 : 1;
        }
        const isUpdate = this._shouldShowUpdateForm();

        if (isUpdate && isDirty !== undefined) {
            this._dirtyComponentCount += isDirty ? 1 : -1;
        }

        const isSaveable = (this._invalidComponentCount === 0) && (isUpdate ? this._dirtyComponentCount > 0 : true);

        if (isSaveable !== this._isSaveEnabled) {
            this._isSaveEnabled = isSaveable;
            this._reloadFooterBar();
        }
    }

    private _reloadFooterBar() {
        this._reloadFooter.value = !this._reloadFooter.value;
    }

    private _draftServiceEndpoint: ServiceEndpoint;
    private _initialEndpointState: ServiceEndpoint;
    private _azureSubscriptionAuthActionCreator: AzureSubscriptionAuthActionCreator;
    private _endpointEditorActionCreator: EndpointsEditorActionsCreator;
    private _endpointEditorStore: EndpointsEditorStore;
    private _isSaveEnabled: boolean;
    private _showAzureSubscriptionAuth: boolean;
    private _reloadFooter: ObservableValue<boolean>;
    private _invalidComponentCount: number;
    private _dirtyComponentCount: number;
    private _isSovereignFeatureEnabled: boolean;
}

VssComponent.register("endpoint-editor-component-kubernetes", KubernetesEndpointEditorComponent);